import React from "react";
import { Link } from "react-router-dom";
import { Alert } from "../../services/alerts.types";
import styles from "./ReportDashboard.module.css";

interface TFn {
  (key: string, options?: Record<string, unknown>): string;
}

export interface AlertCardProps {
  alert: Alert;
  /** Elapsed já interpolado no cliente (segundos), avança a cada tick. */
  liveElapsedSeconds: number;
  t: TFn;
}

const codeOf = (vagaKey: string | null): string => (vagaKey ? vagaKey.replace(/^vaga_?/i, "") : "");

/** Metadados de exibição por tipo de alerta (severidade, ícone, destino de navegação). */
const META: Record<
  string,
  { danger: boolean; icon: string; titleKey: string; bodyKey: string; limit: number; to: string; goKey: string }
> = {
  IdleWithMaterial: {
    danger: true,
    icon: "inventory_2",
    titleKey: "alertIdleTitle",
    bodyKey: "alertIdleBody",
    limit: 5,
    to: "/reports/synoptic",
    goKey: "openSynoptic",
  },
  ExtraVagaOccupied: {
    danger: false,
    icon: "wrong_location",
    titleKey: "alertExtraTitle",
    bodyKey: "alertExtraBody",
    limit: 10,
    to: "/occupancy-map",
    goKey: "openMap",
  },
};

const AlertCard: React.FC<AlertCardProps> = ({ alert, liveElapsedSeconds, t }) => {
  const meta = META[alert.type];
  const minutes = Math.max(0, Math.floor(liveElapsedSeconds / 60));

  // Tipo desconhecido: degrada para um cartão neutro (não quebra se o backend adicionar um tipo).
  if (!meta) {
    return (
      <div className={`${styles.alertCard} ${styles.alertWarning}`}>
        <span className={`${styles.alertIconTile} ${styles.alertIconWarning}`}>
          <span className="material-symbols-outlined">warning</span>
        </span>
        <div className={styles.alertBody}>
          <div className={styles.alertTitleRow}>
            <span className={`${styles.alertTitle} ${styles.alertTitleWarning}`}>{alert.type}</span>
            <span className={styles.durationChip}>
              <span className="material-symbols-outlined">schedule</span>
              {minutes} {t("minShort")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const cardClass = meta.danger ? styles.alertDanger : styles.alertWarning;
  const iconClass = meta.danger ? styles.alertIconDanger : styles.alertIconWarning;
  const titleClass = meta.danger ? styles.alertTitleDanger : styles.alertTitleWarning;
  const goClass = meta.danger ? styles.goHintDanger : styles.goHintWarning;

  const body = t(meta.bodyKey, { vaga: codeOf(alert.vagaKey), min: minutes, limit: meta.limit });

  return (
    <Link to={meta.to} className={`${styles.alertCard} ${cardClass}`}>
      <span className={`${styles.alertIconTile} ${iconClass}`}>
        <span className="material-symbols-outlined">{meta.icon}</span>
      </span>
      <div className={styles.alertBody}>
        <div className={styles.alertTitleRow}>
          <span className={`${styles.alertTitle} ${titleClass}`}>{t(meta.titleKey)}</span>
          <span className={styles.durationChip}>
            <span className="material-symbols-outlined">schedule</span>
            {minutes} {t("minShort")}
          </span>
        </div>
        <span className={styles.alertDetail}>{body}</span>
        <span className={`${styles.goHint} ${goClass}`}>
          {t(meta.goKey)}
          <span className="material-symbols-outlined">arrow_forward</span>
        </span>
      </div>
    </Link>
  );
};

export default AlertCard;
